import { TEnumName, useEnum } from "@/api/services/global/enum";
import { IconClose, IconExpandMore } from "@/assets/icons";
import { useFormField } from "@/components/elements/FormField/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { background, black, blue, button, divider, lightBlue, state, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { TRequiredOne } from "@/types/common";
import { TLanguageCode } from "@/types/enum";
import { findLabelByValue } from "@/utils/object";
import {
  Box,
  CircularProgress,
  FormHelperText,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps,
  Typography,
  css,
  styled,
} from "@mui/material";
import { FC, useMemo } from "react";

export type TSelectColorSize = "md" | "lg";

type TCustomOption = {
  label: string;
  value: string | number;
  color?: string;
  hidden?: boolean;
};

export type TSelectColorProps = {
  size?: TSelectColorSize;
  readonly?: boolean;
  clearable?: boolean;
  previousValue?: string | number;
  helperText?: string;
  nonControl?: boolean;
  loading?: boolean;
  disabledValues?: string[];
  lang?: TLanguageCode;
  colorPrevious?: string;
  optionColors?: {
    [key: string]: {
      bgColor: string;
      color: string;
    };
  };
  onChange?: (value: string) => void;
} & Omit<SelectProps, "size" | "children" | "readOnly" | "multiple" | "onChange"> &
  TRequiredOne<{ options: TCustomOption[]; enumName: TEnumName }>;

export const SelectColor: FC<TSelectColorProps> = ({
  size = "md",
  readonly = false,
  previousValue = false,
  nonControl = false,
  placeholder = "選択してください",
  clearable = true,
  helperText,
  options,
  enumName,
  lang,
  loading = false,
  disabled,
  disabledValues = [],
  colorPrevious = lightBlue[60],
  optionColors,
  ...rest
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const { data, isLoading } = useEnum(enumName, lang ? { lang } : undefined);
  const value = field ? field.value : rest.value;

  const isPrevious = useMemo(() => {
    if (fieldState?.isDirty === false) return true;
    return Boolean(previousValue) && value === previousValue;
  }, [fieldState?.isDirty, previousValue, value]);

  const selectOptions = useMemo(() => options ?? data ?? [], [options, data]);

  const selected = useMemo(() => {
    return [value].flat().flatMap((v) => {
      if (v === undefined || v === null) return [];
      const label = findLabelByValue(selectOptions, v);
      if (!label) return [];
      return label;
    });
  }, [value, selectOptions]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return selectOptions.find((option: TCustomOption) => option.value === value);
  }, [value, selectOptions]);

  const selectedColors = useMemo(() => {
    if (!selectedOption || !optionColors) {
      return { bgColor: background.white, color: text.primary };
    }

    const hasColor = "color" in selectedOption && selectedOption.color;
    if (!hasColor) {
      return { bgColor: background.white, color: text.primary };
    }

    return optionColors[selectedOption.color as string] || { bgColor: background.white, color: text.primary };
  }, [selectedOption, optionColors]);

  const IconComponent = useMemo(() => {
    if (!clearable) return;
    if (selected.length) {
      return () => (
        <IconBox
          sx={{ mr: 1 }}
          onClick={() => {
            rest.onChange?.("");
            field?.onChange(null);
            field?.onBlur();
          }}
        >
          {!readonly && !disabled && <IconClose fontSize={20} color={text.primary} />}
        </IconBox>
      );
    }
    return isLoading || loading ? () => null : IconExpandMore;
  }, [isLoading, loading, selected, readonly, disabled, clearable]);

  const onChange = (e: SelectChangeEvent<unknown>) => {
    rest.onChange?.(e.target.value as string);
    field?.onChange(e);
    field?.onBlur();
  };

  return (
    <Box position="relative">
      <StyledSelect
        error={Boolean(fieldState?.error)}
        selectSize={size}
        readonly={readonly}
        previous={isPrevious}
        hasValue={selected.length > 0}
        displayEmpty
        IconComponent={IconComponent}
        {...rest}
        {...field}
        value={value ?? null}
        disabled={disabled}
        colorPrevious={colorPrevious}
        selectedBgColor={selectedColors.bgColor}
        selectedColor={selectedColors.color}
        onChange={onChange}
        renderValue={() => {
          if (selected.length === 0) {
            return (
              <Typography variant="cap12" color={`${text.tertiary}!important`}>
                {placeholder}
              </Typography>
            );
          }

          return selected.join(", ");
        }}
      >
        {selectOptions.map((option: TCustomOption) => {
          const isDisable = "hidden" in option && option.hidden === true;
          const isHidden = isDisable && value !== option.value;
          const color = option.color ? optionColors?.[option.color]?.color : "#000";
          const bgColor = option.color ? optionColors?.[option.color]?.bgColor : "#fff";

          return (
            !isHidden && (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={isDisable || disabledValues.includes(option?.value?.toString())}
                sx={{ bgcolor: bgColor, color: color + "!important", mt: "5px", border: bgColor === "#FFFFFF" ? `1px solid ${black[95]}` : "none" }}
              >
                {option.label}
              </MenuItem>
            )
          );
        })}
      </StyledSelect>
      {(isLoading || loading) && (
        <LoadingWrap>
          <CircularProgress color="inherit" size={14} />
        </LoadingWrap>
      )}
      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};

const options = {
  shouldForwardProp: (propName: string) =>
    !["selectSize", "previous", "readonly", "hasValue", "selectedBgColor", "selectedColor", "colorPrevious"].includes(propName),
};

const StyledSelect = styled(MuiSelect, options)<{
  selectSize: TSelectColorSize;
  readonly: boolean;
  previous: boolean;
  hasValue: boolean;
  colorPrevious?: string;
  selectedBgColor?: string;
  selectedColor?: string;
}>`
  &.MuiInputBase-root {
    position: relative;
    background: ${background.white};
    &::placeholder {
      color: ${text.tertiary};
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.md};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-focused {
      &::after {
        content: "";
        position: absolute;
        top: -4px;
        left: -4px;
        width: 100%;
        height: 100%;
        padding: 2px;
        border: 2px solid ${button.secondary};
        box-sizing: content-box;
        border-radius: ${rounded.sm};
        z-index: -1;
      }
    }

    &.Mui-error {
      background: ${background.error};
      fieldset {
        border-color: ${state.error_1}!important;
      }
    }
    &.Mui-disabled {
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    .MuiSelect-icon {
      transform: none !important;
    }

    ${({ selectSize, hasValue }) => {
      if (selectSize === "md") return MediumInput(hasValue);
      if (selectSize === "lg") return LargeInput;
      return MediumInput(hasValue);
    }};
    ${({ readonly }) => readonly && readonlyInput};
    ${({ previous, colorPrevious }) =>
      previous &&
      css`
        .MuiSelect-select {
          color: ${colorPrevious};
        }
      `};

    ${({ selectedBgColor, selectedColor }) =>
      selectedBgColor &&
      selectedColor &&
      css`
        .MuiInputBase-input {
          background-color: ${selectedBgColor} !important;
          color: ${selectedColor} !important;
        }
      `};
  }
`;

const readonlyInput = css`
  background: ${background.disable};
  pointer-events: none;
  fieldset {
    border-color: ${divider.middle}!important;
  }
  &::placeholder {
    opacity: 0;
  }
`;

const MediumInput = (hasValue: boolean) => css`
  .MuiInputBase-input {
    padding: 4px 32px 4px 10px;
    border-radius: ${hasValue ? `${rounded.md} !important` : "none !important"};
    ${css(typography.cap12)};
  }
`;

const LargeInput = css`
  .MuiInputBase-input {
    padding: 12px 8px;
    ${css(typography.body14)};
  }
`;

const LoadingWrap = styled(Box)`
  position: absolute;
  display: block;
  top: 0;
  right: 0;
  background-color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 30px;
`;
